
import React from "react";
import { useWebhookLogs } from "@/hooks/useWebhookLogs";
import { Loader2, Clock } from "lucide-react";
import { formatDistance } from "date-fns";
import { nl } from "date-fns/locale";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";

interface WebhookLogsProps {
  notificationId: string;
}

const getStatusInfo = (status: number, success: boolean) => {
  if (success && status === 200) {
    return { text: "Verzonden", variant: "success" as const };
  } else if (status >= 400) {
    return { text: "Error", variant: "destructive" as const };
  } else {
    return { text: `Status ${status}`, variant: "secondary" as const };
  }
};

const getLatencyVariant = (responseTimeMs: number | null | undefined): "success" | "default" | "destructive" => {
  if (!responseTimeMs) return "default";
  if (responseTimeMs < 2000) return "success";
  if (responseTimeMs < 10000) return "default";
  return "destructive";
};

const WebhookTypeLabel = ({ type }: { type: string }) => {
  const labels: Record<string, string> = {
    'decoprof_telegram': 'Telegram',
    'email_recipients': 'E-mail',
    'inkoop_email': 'Inkoop E-mail',
    'decoprof_status': 'Status Webhook',
  };
  
  return (
    <Badge variant='secondary' className="mr-2">
      {labels[type] || type}
    </Badge>
  );
};

const WebhookLogs = ({ notificationId }: WebhookLogsProps) => {
  const { data: logs, isLoading } = useWebhookLogs(notificationId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-4 text-muted-foreground">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
        <span>Webhook logs laden...</span>
      </div>
    );
  }

  if (!logs?.length) {
    return null;
  }

  return (
    <div className="mt-6 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Notificatie Logs</h3>
      </div>
      <div className="border rounded-md overflow-hidden divide-y">
        {logs.map((log) => {
          const statusInfo = getStatusInfo(log.response_status, log.success);
          const edgeMeta = log.request_payload?.edge_meta;
          const responseTimeMs = edgeMeta?.response_time_ms;
          
          return (
            <div key={log.id} className="bg-white p-4">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <WebhookTypeLabel type={log.webhook_type} />
                  <span className="text-sm text-muted-foreground">
                    {formatDistance(new Date(log.created_at), new Date(), {
                      addSuffix: true,
                      locale: nl,
                    })}
                  </span>
                  {responseTimeMs !== null && responseTimeMs !== undefined && (
                    <Badge variant={getLatencyVariant(responseTimeMs)} className="flex items-center gap-1 text-xs">
                      <Clock className="h-3 w-3" />
                      {responseTimeMs}ms
                    </Badge>
                  )}
                </div>
                
                <Badge variant={statusInfo.variant} className="px-2 py-1">
                  {statusInfo.text}
                </Badge>
              </div>
              
              {log.webhook_url && (
                <div className="text-xs text-muted-foreground mb-2 space-y-1">
                  <div className="truncate">Kanaal: {log.webhook_url}</div>
                </div>
              )}

              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="payload">
                  <AccordionTrigger className="text-sm py-2">
                    Volledige data bekijken
                  </AccordionTrigger>
                  <AccordionContent>
                    <pre className="p-3 bg-slate-50 rounded-md text-xs overflow-auto max-h-96">
                      {JSON.stringify(log.request_payload, null, 2)}
                    </pre>
                  </AccordionContent>
                </AccordionItem>
                
                {log.response_body && (
                  <AccordionItem value="response">
                    <AccordionTrigger className="text-sm py-2">
                      Server antwoord bekijken
                    </AccordionTrigger>
                    <AccordionContent>
                      <pre className="p-3 bg-slate-50 rounded-md text-xs overflow-auto max-h-96">
                        {log.response_body}
                      </pre>
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WebhookLogs;
